import axios from 'axios'

import { NOT_AUTHENTICATED_DETAIL } from '@/apiClient/feErrorCodes'
import type { IPublicGptMeta } from '@/apiClient/types/publicGpt'
import { useAppStore } from '@/store/app'
import { EMBED_QUERY_FLAG, isFramed } from '@/utils/embed'

/** Route path (name `PublicGpt`) for the standalone public GPT page. */
export const publicGptRoutePath = (shareToken: string) =>
  `/public/gpt/${encodeURIComponent(shareToken)}`

/**
 * Absolute app URL for a path relative to the deploy base — the app's own origin plus
 * `BASE_URL`, with exactly one slash between them. There is no `APP_BASE_URL` env var in
 * this project, so the origin always comes from `window.location`.
 */
export function appAbsoluteUrl(relativePath: string): string {
  const base = import.meta.env.BASE_URL || '/'
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return new URL(
    `${normalizedBase}${relativePath.replace(/^\//, '')}`,
    window.location.origin,
  ).href
}

/**
 * Absolute, shareable URL for a public GPT link — the app's own origin, mirroring
 * `websitePublicHref`. There is no `APP_BASE_URL` env var in this project.
 */
export function publicGptShareHref(shareToken: string): string {
  return appAbsoluteUrl(publicGptRoutePath(shareToken))
}

/**
 * May this visitor be sent into the full EverGPT app for this GPT?
 *
 * `meta.is_authorized` is not that answer on its own: the backend sets it for every
 * user whose role is not PENDING, which includes Foundever users who have no EverGPT
 * access at all. Acting on it alone navigated those visitors to `/?gptUuid=...` — a
 * protected route — where `noAccessGuard` bounced them to /not-authorised, so a
 * public link was a dead end for exactly the audience it exists for.
 *
 * `isUserAuthorisedForEverGpt` is the app's own answer, set from the bootstrap
 * `GET /users/me` that the standalone page always awaits before it renders, so both
 * signals have to agree before we navigate away from a page that already works.
 *
 * The embedded widget seeds a host token without ever calling `/users/me`, so the
 * flag keeps its optimistic default there — embed never auto-redirects anyway (see
 * `PublicGpt/Index.vue`), and its only affected surface is the top-bar link.
 */
export function canOpenGptInApp(
  meta: Pick<IPublicGptMeta, 'is_authorized'> | null | undefined,
): boolean {
  if (!meta?.is_authorized) return false
  return useAppStore().isUserAuthorisedForEverGpt
}

/**
 * Send the visitor into the full EverGPT app for this GPT.
 *
 * Framed, this opens a NEW TAB instead of navigating. It used to assign
 * `window.top.location`, which yanked the host page — someone else's intranet page —
 * away from under the visitor; and cross-origin browsers block that navigation
 * without a user gesture anyway, silently, so the automatic call simply stalled.
 * Loading the full app inside a widget bubble is no good either, hence a new tab.
 */
export function openGptInApp(gptId: string): void {
  const base = `${window.location.origin}${import.meta.env.BASE_URL || '/'}`
  const target = new URL(base)
  target.searchParams.set('gptUuid', gptId)

  if (isFramed()) {
    window.open(target.href, '_blank', 'noopener')
    return
  }

  window.location.href = target.href
}

/**
 * `<iframe>` snippet an admin can paste to embed the public GPT (framing is allowed for
 * `'self'` + `*.foundever.com` — see the CSP `frame-ancestors` list).
 *
 * Carries `?embed=1` like the widget loader's frame does. The app detects framing on
 * its own, so this is belt and braces for the snippet's readers rather than the thing
 * that makes it work — embeds pasted before this existed behave identically.
 */
export function publicGptEmbedSnippet(shareToken: string): string {
  const src = `${publicGptShareHref(shareToken)}?${EMBED_QUERY_FLAG}=1`
  return [
    `<iframe src="${src}"`,
    `  width="100%" height="640"`,
    `  style="border:0;border-radius:12px"`,
    `  title="EverGPT" allow="clipboard-write"></iframe>`,
  ].join('\n')
}

/**
 * Absolute URL of the embeddable widget loader (`/widget/embed.v1.js`), on the app's
 * own origin and honouring any deploy base path. Built by `vite.embed.config.ts`.
 */
export function publicGptWidgetScriptSrc(): string {
  return appAbsoluteUrl('widget/embed.v1.js')
}

/**
 * `<script>` snippet an admin pastes onto any internal `*.foundever.com` page to add a
 * floating chat button for this GPT (the embeddable widget, not a full-page iframe).
 */
export function publicGptWidgetSnippet(shareToken: string): string {
  const src = publicGptWidgetScriptSrc()
  return [
    `<script src="${src}"`,
    `  data-share-token="${shareToken}"`,
    `  async></script>`,
  ].join('\n')
}

/**
 * True when a public GPT request failed because the link/GPT is no longer available
 * (link deactivated or GPT deleted). The backend answers `404` (`GPTNotFoundError`);
 * `410` is accepted too for forward compatibility.
 */
export function isPublicGptGoneError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return status === 404 || status === 410
}

/** True when the request was rate-limited (`429`). */
export function isRateLimitedError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429
}

/**
 * True when the request failed because the visitor has no usable session — the token
 * expired, was never issued, or the backend rejected it.
 *
 * Worth telling apart from a generic failure: these endpoints take any valid
 * Foundever JWT and check no role, so a 401 here is never "you may not see this
 * GPT", it is "sign in again". Lumping it into the generic error told the visitor
 * the GPT could not be loaded and left them with nothing to do about it. The `403`
 * arm matches the backend's authentication (not authorization) answer; a 403 with
 * any other body stays a genuine refusal.
 */
export function isPublicGptAuthError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false

  const status = error.response?.status
  if (status === 401) return true
  if (status !== 403) return false

  const detail = (error.response?.data as { detail?: unknown } | undefined)
    ?.detail
  return detail === NOT_AUTHENTICATED_DETAIL
}
